import {
  CalendarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  HomeIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { stringify } from 'qs';
import React from 'react';
import { useNavigate } from 'hotel/navigation';

import { SecondaryButton } from 'components/Button';
import StatusBadge from 'components/StatusBadge';
import { TripType, TripStatus } from 'generated/graphql';
import { useUserRole } from 'hooks/useUserRole';

interface TripCardProps {
  trip: {
    id: string;
    tripType: TripType;
    status: TripStatus;
    checkInDate: string;
    checkOutDate: string;
    createdAt: string;
    controlNumber?: string | null;
    hotels: Array<{
      name: string;
      address?: {
        city?: string | null;
        state?: string | null;
      } | null;
    }>;
    rooms: number;
    guests: number;
    hasUnknownGuests: boolean;
  };
}

const TripCard: React.FC<TripCardProps> = ({ trip }) => {
  const navigate = useNavigate();
  const { isOnlyTraveler } = useUserRole();
  const hideGroupDetails = isOnlyTraveler && trip.tripType === 'GROUP_REQUEST';

  const getTripId = () => {
    return `${trip.tripType === 'SELF_BOOKING' ? 'Trip' : 'Request'} #${trip.controlNumber || trip.id}`;
  };

  const toTitleCase = (text: string) => {
    return text
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Format date string without timezone conversion
  // Treats date-only strings (YYYY-MM-DD) as calendar dates
  const formatDateString = (dateString: string): string => {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    });
  };

  const handleCardClick = () => {
    if (trip.tripType === 'SELF_BOOKING') {
      // Navigate to booking confirmation page for self bookings
      navigate(`/booking-confirmation/${trip.id}`);
    } else {
      // Navigate to group request details for group requests
      const requestId = trip.id.replace('group_', '');
      navigate(`/requests/${requestId}`);
    }
  };

  const handleViewRoster = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click event
    if (trip.controlNumber) {
      const filterString = stringify({ orderNumber: trip.controlNumber });
      navigate(`/roster?filters=${filterString}`);
    }
  };

  // For group requests, make the entire card clickable
  if (trip.tripType === 'GROUP_REQUEST') {
    return (
      <button
        onClick={handleCardClick}
        className="w-full rounded-lg border border-gray-200 bg-white p-6 text-left shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
      >
        {/* Top row: Status badges and booked date */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <StatusBadge status={trip.status} />
            <span className="inline-block rounded bg-black px-2 py-1 text-xs font-medium uppercase text-white">
              Group Trip
            </span>
          </div>
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <CheckCircleIcon className="size-5 text-accentGreen" />
            {trip.status === TripStatus.Confirmed
              ? 'Booked'
              : 'Requested'} on {new Date(trip.createdAt).toLocaleDateString()}
          </div>
        </div>

        {/* Trip title with location */}
        <div className="mb-4">
          <div className="text-left">
            <span className="font-bold text-gray-900">{getTripId()}</span>
            <span className="font-normal text-gray-500">
              {' '}
              {trip.hotels[0]?.name && toTitleCase(trip.hotels[0].name)}
              {trip.hotels[0]?.address &&
                ` in ${toTitleCase(trip.hotels[0].address.city || '')}, ${trip.hotels[0].address.state}`}
            </span>
          </div>
        </div>

        {/* Trip Details with icons and missing guest warning - with top border */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6 text-sm text-gray-900">
              <div className="flex items-center gap-1">
                <CalendarIcon className="size-5 text-accentGreen" />
                <span>Check-in: {formatDateString(trip.checkInDate)}</span>
              </div>
              <div className="flex items-center gap-1">
                <CalendarIcon className="size-5 text-accentGreen" />
                <span>Check-out: {formatDateString(trip.checkOutDate)}</span>
              </div>
              {!hideGroupDetails && (
                <>
                  <div className="flex items-center gap-1">
                    <HomeIcon className="size-5 text-accentGreen" />
                    <span>{trip.rooms} Rooms</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <UserGroupIcon className="size-5 text-accentGreen" />
                    <span>{trip.guests} Guests</span>
                  </div>
                </>
              )}
            </div>

            {/* Missing guest warning and View Roster button */}
            {!hideGroupDetails && (
              <div className="flex items-center gap-4">
                {trip.hasUnknownGuests && (
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="mr-1 size-4 text-yellow-500" />
                    <span className="text-sm font-medium text-yellow-600">
                      Missing guest list
                    </span>
                  </div>
                )}
                {trip.controlNumber && (
                  <SecondaryButton size="sm" onClick={handleViewRoster}>
                    View Roster
                  </SecondaryButton>
                )}
              </div>
            )}
          </div>
        </div>
      </button>
    );
  }

  // For self bookings, keep the original card structure with clickable title
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      {/* Top row: Status badges and booked date */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StatusBadge status={trip.status} />
        </div>
        <div className="flex items-center gap-1 text-sm text-gray-500">
          <CheckCircleIcon className="size-5 text-accentGreen" />
          Booked on {new Date(trip.createdAt).toLocaleDateString()}
        </div>
      </div>

      {/* Trip title with location */}
      <div className="mb-4">
        <button
          onClick={handleCardClick}
          className="rounded text-left text-base hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        >
          <span className="font-bold text-gray-900">{getTripId()}</span>
          <span className="font-normal text-gray-500">
            {' '}
            {trip.hotels[0]?.name && toTitleCase(trip.hotels[0].name)}
            {trip.hotels[0]?.address &&
              ` in ${toTitleCase(trip.hotels[0].address.city || '')}, ${trip.hotels[0].address.state}`}
          </span>
        </button>
      </div>

      {/* Trip Details with icons and missing guest warning - with top border */}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6 text-sm text-gray-900">
            <div className="flex items-center gap-1">
              <CalendarIcon className="size-5 text-accentGreen" />
              <span>Check-in: {formatDateString(trip.checkInDate)}</span>
            </div>
            <div className="flex items-center gap-1">
              <CalendarIcon className="size-5 text-accentGreen" />
              <span>Check-out: {formatDateString(trip.checkOutDate)}</span>
            </div>
            <div className="flex items-center gap-1">
              <HomeIcon className="size-5 text-accentGreen" />
              <span>{trip.rooms} Rooms</span>
            </div>
            <div className="flex items-center gap-1">
              <UserGroupIcon className="size-5 text-accentGreen" />
              <span>{trip.guests} Guests</span>
            </div>
          </div>

          {/* Missing guest warning and View Roster button */}
          <div className="flex items-center gap-4">
            {trip.hasUnknownGuests && (
              <div className="flex items-center">
                <ExclamationTriangleIcon className="mr-1 size-4 text-yellow-500" />
                <span className="text-sm font-medium text-yellow-600">
                  Missing guest list
                </span>
              </div>
            )}
            {trip.controlNumber && (
              <SecondaryButton size="sm" onClick={handleViewRoster}>
                View Roster
              </SecondaryButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripCard;
